import {
  Accordion,
  AccordionItem,
  Button,
  DatePickerField,
  DropzoneReadonly,
  FiFileText,
  FiPlusCircle,
  FiTrash2,
  GridItem,
  NumericFormatField,
  NumericReadOnlyField,
  ReadOnlyField,
  SelectField,
  SimpleGrid,
  TextField,
} from '@agrotoken-tech/ui';
import { formatDate } from '@bcba/common-utils';
import { S3FileUploader } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType, geoDocumentationMimeTypes } from '@bcba/types';
import { getUrlFileFromAws } from '../../../../../../services/aws/getUrlFileFromAws';
import { getS3SignedUrl } from '../../../../../../services/documents/getS3SignedUrl';
import { FormSection } from '../../../../shared';
import { useCropPledgeFarmContext } from '../../context/CropPledgeFarmInformationContext';

const LotsDataForm = () => {
  const { t } = useTranslation();
  const {
    formMethods,
    lotsFields,
    onAddLotAffidavit,
    onRemoveLotAffidavit,
    cropsOptions,
    data,
    isFormReadOnly,
  } = useCropPledgeFarmContext();

  const { resetField, setValue, watch } = formMethods;

  return (
    <GridItem colSpan={2}>
      <FormSection
        title={t('cropPledge.lotAffidavitsData.title')}
        action={
          !isFormReadOnly && (
            <Button
              variant={'linkPrimary'}
              leftIcon={<FiPlusCircle />}
              onClick={onAddLotAffidavit}
              size={'sm'}
            >
              {t('cropPledge.lotAffidavitsData.addAffidavit')}
            </Button>
          )
        }
      >
        <Accordion
          allowMultiple
          index={lotsFields.map((_, index) => index)}
          size="sm"
        >
          {lotsFields.map((lot, index) => {
            const prefix = `lots.${index}` as const;

            const lotInDb = data?.lotsInformation?.find(
              (l) => l.uuid === lot.uuid,
            );

            const fileName = lot?.geolocationDocument?.originalName || '';

            const documentFromDatabase = lotInDb?.geolocationDocument;

            const shouldShowReadOnlyDropzoneField =
              isFormReadOnly ||
              (documentFromDatabase &&
                watch(`${prefix}.geolocationDocument.uuid`) ===
                  documentFromDatabase.uuid);

            return (
              <AccordionItem
                key={lot.uuid}
                title={t('cropPledge.lotAffidavitsData.lotNumber').replace(
                  '{{lotNumber}}',
                  String(index + 1),
                )}
                HeadButton={() =>
                  !isFormReadOnly && (
                    <Button
                      variant="linkDestructive"
                      onClick={() => onRemoveLotAffidavit(index)}
                      leftIcon={<FiTrash2 />}
                      mr={4}
                      size="sm"
                    >
                      {t('common.delete')}
                    </Button>
                  )
                }
              >
                <SimpleGrid columns={2} mt="6" spacing={6}>
                  <TextField
                    name={`${prefix}.identifier`}
                    label={t(
                      'cropPledge.lotAffidavitsData.form.lotIdentifier.label',
                    )}
                    placeholder={t(
                      'cropPledge.lotAffidavitsData.form.lotIdentifier.placeholder',
                    )}
                    errorMessageFormatter={t}
                    rhfOptions={{ required: true }}
                    isReadOnly={isFormReadOnly}
                    readOnlyValue={
                      lotInDb?.identifier ?? t('common.toBeCompletedByBolsa')
                    }
                  />
                  <ReadOnlyField
                    label={t(
                      'cropPledge.lotAffidavitsData.form.areaPercentage.label',
                    )}
                    value={
                      lotInDb?.areaPercentage?.toString() ??
                      t('common.toBeCompletedByBolsa')
                    }
                  />
                  <GridItem colSpan={2}>
                    {shouldShowReadOnlyDropzoneField ? (
                      <DropzoneReadonly
                        fileName={fileName}
                        label={t('cropPledge.farmInfo.form.geolocation.label')}
                        onClick={async () => {
                          if (documentFromDatabase?.uuid) {
                            const url = await getUrlFileFromAws(
                              documentFromDatabase.uuid,
                            );
                            window.open(url, '_blank');
                          }
                        }}
                        {...(isFormReadOnly
                          ? {}
                          : {
                              onDelete: () => {
                                setValue(
                                  `${prefix}.geolocationDocument.uuid`,
                                  undefined,
                                  {
                                    shouldDirty: true,
                                  },
                                );
                                setValue(
                                  `${prefix}.geolocationDocument.file`,
                                  undefined,
                                  {
                                    shouldDirty: true,
                                  },
                                );
                              },
                            })}
                        leftIcon={FiFileText}
                      />
                    ) : (
                      <S3FileUploader
                        fieldPrefix={`${prefix}.geolocationDocument.file.s3TempKey`}
                        label={t(
                          'cropPledge.lotAffidavitsData.form.geolocation.label',
                        )}
                        getS3PreSignedUrl={getS3SignedUrl}
                        onFileUploadSuccess={async (
                          fileName,
                          mimeType,
                          s3TempKey,
                        ) => {
                          setValue(
                            `${prefix}.geolocationDocument.file.name`,
                            fileName,
                            {
                              shouldDirty: true,
                            },
                          );
                          setValue(
                            `${prefix}.geolocationDocument.file.mimeType`,
                            mimeType as FileMimeType,
                            { shouldDirty: true },
                          );
                          setValue(
                            `${prefix}.geolocationDocument.file.s3TempKey`,
                            s3TempKey,
                            {
                              shouldDirty: true,
                            },
                          );
                          setValue(
                            `${prefix}.geolocationDocument.keepOriginal`,
                            false,
                            {
                              shouldDirty: true,
                            },
                          );
                        }}
                        mimeTypes={geoDocumentationMimeTypes}
                        messages={{
                          supportedFileTypes: t(
                            'cropPledge.lotAffidavitsData.form.geolocation.supportedFileTypes',
                          ),
                        }}
                        onDeleteClick={() => {
                          resetField(
                            `${prefix}.geolocationDocument.file.s3TempKey`,
                          );
                          resetField(`${prefix}.geolocationDocument.file.name`);
                          resetField(
                            `${prefix}.geolocationDocument.file.mimeType`,
                          );
                          resetField(
                            `${prefix}.geolocationDocument.keepOriginal`,
                            { defaultValue: false },
                          );
                        }}
                        required
                      />
                    )}
                  </GridItem>
                  <ReadOnlyField
                    label={t(
                      'cropPledge.lotAffidavitsData.form.latitudeOfBarycenter.label',
                    )}
                    value={
                      lotInDb?.latitude ?? t('common.toBeCompletedByBolsa')
                    }
                  />
                  <ReadOnlyField
                    label={t(
                      'cropPledge.lotAffidavitsData.form.longitudeOfBarycenter.label',
                    )}
                    value={
                      lotInDb?.longitude ?? t('common.toBeCompletedByBolsa')
                    }
                  />
                  <SelectField
                    name={`${prefix}.affidavit.productType`}
                    label={t('cropPledge.lotAffidavitsData.form.crop.label')}
                    placeholder={t(
                      'cropPledge.lotAffidavitsData.form.crop.placeholder',
                    )}
                    errorMessageFormatter={t}
                    options={cropsOptions}
                    defaultInputValue={
                      lotInDb?.affidavit?.product
                        ? t(`product.${lotInDb.affidavit.product}`)
                        : undefined
                    }
                    rhfOptions={{ required: true }}
                    isReadOnly={isFormReadOnly}
                    readOnlyValue={
                      lotInDb?.affidavit?.product
                        ? t(`product.${lotInDb.affidavit.product}`)
                        : '-'
                    }
                  />
                  {isFormReadOnly ? (
                    <>
                      <ReadOnlyField
                        label={t(
                          'cropPledge.lotAffidavitsData.form.startDate.label',
                        )}
                        value={
                          lotInDb?.affidavit?.startsAt
                            ? formatDate(lotInDb.affidavit.startsAt)
                            : ''
                        }
                      />
                      <ReadOnlyField
                        label={t(
                          'cropPledge.lotAffidavitsData.form.endDate.label',
                        )}
                        value={
                          lotInDb?.affidavit?.endsAt
                            ? formatDate(lotInDb.affidavit.endsAt)
                            : ''
                        }
                      />
                    </>
                  ) : (
                    <>
                      <DatePickerField
                        name={`${prefix}.affidavit.startsAt`}
                        label={t(
                          'cropPledge.lotAffidavitsData.form.startDate.label',
                        )}
                        placeholder={t(
                          'cropPledge.lotAffidavitsData.form.startDate.placeholder',
                        )}
                        errorMessageFormatter={t}
                        rhfOptions={{ required: true }}
                      />
                      <DatePickerField
                        name={`${prefix}.affidavit.endsAt`}
                        label={t(
                          'cropPledge.lotAffidavitsData.form.endDate.label',
                        )}
                        placeholder={t(
                          'cropPledge.lotAffidavitsData.form.endDate.placeholder',
                        )}
                        errorMessageFormatter={t}
                        rhfOptions={{ required: true }}
                      />
                    </>
                  )}

                  <NumericFormatField
                    name={`${prefix}.affidavit.averageYieldPreviousSeason`}
                    label={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason.label',
                    )}
                    placeholder={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason.placeholder',
                    )}
                    errorMessageFormatter={t}
                    isReadOnly={isFormReadOnly}
                    readOnlyValue={
                      lotInDb?.affidavit?.averageYieldPreviousSeason?.toString() ??
                      '-'
                    }
                  />
                  <NumericFormatField
                    name={`${prefix}.affidavit.averageYieldPreviousSeason1`}
                    label={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason1.label',
                    )}
                    placeholder={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason1.placeholder',
                    )}
                    errorMessageFormatter={t}
                    isReadOnly={isFormReadOnly}
                    readOnlyValue={
                      lotInDb?.affidavit?.averageYieldPreviousSeason1?.toString() ??
                      '-'
                    }
                  />
                  <NumericFormatField
                    name={`${prefix}.affidavit.averageYieldPreviousSeason2`}
                    label={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason2.label',
                    )}
                    placeholder={t(
                      'cropPledge.lotAffidavitsData.form.averageYieldPreviousSeason2.placeholder',
                    )}
                    errorMessageFormatter={t}
                    isReadOnly={isFormReadOnly}
                    readOnlyValue={
                      lotInDb?.affidavit?.averageYieldPreviousSeason2?.toString() ??
                      '-'
                    }
                  />
                  <NumericReadOnlyField
                    label={t(
                      'cropPledge.lotAffidavitsData.form.estimatedLotProductionAmount.label',
                    )}
                    value={
                      lotInDb?.affidavit?.estimatedProductionValue?.toString() ??
                      t('common.toBeCompletedByBolsa')
                    }
                  />
                </SimpleGrid>
              </AccordionItem>
            );
          })}
        </Accordion>
      </FormSection>
    </GridItem>
  );
};

export default LotsDataForm;
